import React from 'react';

// Scale factor: 1080x1350 → preview size
const SCALE = 0.4;
const W = 1080;
const H = 1350;

const previewContainer = {
  width: W * SCALE,
  height: H * SCALE,
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 12,
  flexShrink: 0,
};

const innerCanvas = {
  width: W,
  height: H,
  transform: `scale(${SCALE})`,
  transformOrigin: 'top left',
  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  background: 'radial-gradient(ellipse at center, #1a2235 0%, #111827 40%, #0A0F1A 100%)',
};

// ---------- Tweet Card Preview ----------

export const TweetCardPreview = ({ text, author, handle, imageUrl, slideNumber, totalSlides, brandHandle = "@IndieChess" }) => {
  const textLen = (text || '').length;
  let fontSize = 40;
  if (textLen > 200) fontSize = 32;
  else if (textLen > 140) fontSize = 36;

  return (
    <div style={previewContainer}>
      <div style={innerCanvas}>
        {/* Main content area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '100px 64px 120px' }}>
          {/* Tweet card */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            borderRadius: 24,
            padding: '52px 48px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
          }}>
            {/* Gold left accent bar */}
            <div style={{
              position: 'absolute', top: 24, bottom: 24, left: 0, width: 5,
              borderRadius: 3, background: 'linear-gradient(180deg, #FFD700 0%, #C8A800 100%)',
            }} />

            {/* Author row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 40 }}>
              {imageUrl ? (
                <img src={imageUrl} alt="" style={{
                  width: 100, height: 100, borderRadius: 50, objectFit: 'cover',
                  border: '3px solid rgba(255, 215, 0, 0.3)',
                }} />
              ) : (
                <div style={{
                  width: 100, height: 100, borderRadius: 50,
                  background: 'linear-gradient(135deg, #C8A800 0%, #FFD700 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 42, fontWeight: 800, color: '#0A0F1A',
                }}>
                  {(author || '?').charAt(0)}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 40, fontWeight: 700, color: '#FFFFFF' }}>{author}</span>
                <span style={{ fontSize: 30, color: 'rgba(255, 255, 255, 0.5)' }}>{handle}</span>
              </div>
              {/* X icon */}
              <div style={{ marginLeft: 'auto' }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.4)">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </div>
            </div>

            {/* Tweet text */}
            <p style={{ fontSize, fontWeight: 500, color: '#FFFFFF', lineHeight: 1.5, margin: 0 }}>
              {text}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ position: 'absolute', bottom: 44, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'rgba(255, 255, 255, 0.35)', letterSpacing: 2 }}>
            {brandHandle}
          </span>
        </div>
      </div>
    </div>
  );
};

// ---------- Quote Card Preview ----------

export const QuoteCardPreview = ({ text, quoteNumber, totalQuotes, brandHandle = "@IndieChess" }) => {
  const textLen = (text || '').length;
  let fontSize = 48;
  if (textLen > 240) fontSize = 34;
  else if (textLen > 180) fontSize = 38;
  else if (textLen > 120) fontSize = 42;

  return (
    <div style={previewContainer}>
      <div style={innerCanvas}>
        {/* Main content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px 56px 120px' }}>
          {/* Quote number badge */}
          <div style={{
            width: 72, height: 72, borderRadius: 36,
            background: 'linear-gradient(135deg, #C8A800 0%, #FFD700 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, fontWeight: 900, color: '#0A0F1A',
            marginBottom: 36, boxShadow: '0 0 24px rgba(255, 215, 0, 0.25)',
          }}>
            {quoteNumber}
          </div>

          {/* Quote card */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            borderRadius: 28, padding: '64px 56px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative', maxWidth: 940, width: '100%',
          }}>
            {/* Opening quote mark */}
            <div style={{
              position: 'absolute', top: 12, left: 36,
              fontSize: 110, fontFamily: 'Georgia, serif',
              color: 'rgba(255, 215, 0, 0.2)', lineHeight: 1,
            }}>
              {'\u201C'}
            </div>

            <p style={{
              fontSize, fontWeight: 500, fontStyle: 'italic', color: '#FFFFFF',
              textAlign: 'center', lineHeight: 1.45, margin: 0, marginTop: 24,
              padding: '0 12px', position: 'relative', zIndex: 1,
            }}>
              {text}
            </p>

            {/* Closing quote mark */}
            <div style={{
              position: 'absolute', bottom: -4, right: 36,
              fontSize: 110, fontFamily: 'Georgia, serif',
              color: 'rgba(255, 215, 0, 0.2)', lineHeight: 1,
            }}>
              {'\u201D'}
            </div>
          </div>

          {/* Mystery prompt */}
          <div style={{ marginTop: 36, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 36, height: 3, borderRadius: 2, background: 'linear-gradient(90deg, transparent, #FFD700)' }} />
            <span style={{ fontSize: 20, fontWeight: 700, color: '#FFD700', letterSpacing: 2, textTransform: 'uppercase' }}>
              Who said this?
            </span>
            <div style={{ width: 36, height: 3, borderRadius: 2, background: 'linear-gradient(90deg, #FFD700, transparent)' }} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ position: 'absolute', bottom: 44, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'rgba(255, 255, 255, 0.35)', letterSpacing: 2 }}>
            {brandHandle}
          </span>
        </div>
      </div>
    </div>
  );
};

// ---------- Preview Modal ----------

export const PreviewModal = ({ item, type, onClose, slideNumber, totalSlides }) => {
  if (!item) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', cursor: 'default' }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: -36, right: 0,
            background: 'none', border: 'none', color: '#9CA3AF',
            fontSize: 14, cursor: 'pointer', padding: '4px 8px',
          }}
        >
          ESC to close
        </button>

        {/* Scaled-up preview (50% of original = 540x675) */}
        <div style={{ transform: 'scale(1.25)', transformOrigin: 'center center' }}>
          {type === 'tweet' ? (
            <TweetCardPreview
              text={item.text}
              author={item.author}
              handle={item.handle}
              imageUrl={item.imageUrl}
              slideNumber={slideNumber}
              totalSlides={totalSlides}
            />
          ) : (
            <QuoteCardPreview
              text={item.text}
              quoteNumber={slideNumber}
              totalQuotes={totalSlides}
            />
          )}
        </div>
      </div>
    </div>
  );
};
